import { useState, useEffect, useCallback } from 'react';
import { getRequest } from '../../../../../config';

/**
 * Fetches a temp module by its slug (or name) including all sections,
 * questions, and existing answers for the given company.
 *
 * Uses GET /modules/temp-modules/by-name/:moduleSlug?companyId=xxx
 *
 * The response is shaped exactly like company.modules[tab] so it can be
 * dropped directly into QuestionAnswersPage without any UI changes.
 */
export function useModuleData(companyId, moduleSlug, company) {
    const [state, setState] = useState({
        module: null,
        sections: [],
        loading: true,
        error: null,
    });

    const moduleName = moduleSlug.includes('business-audit') ? `${moduleSlug.replace(`-${company?.['testType']}`, '')}` : moduleSlug;

    const fetchData = useCallback(async () => {
        if (!moduleName || !companyId) return;
        setState(prev => ({ ...prev, loading: true, error: null }));

        try {
            const isValidCompanyId = typeof companyId === 'string' && companyId.trim().length > 0;
            const query = isValidCompanyId ? `?companyId=${encodeURIComponent(companyId)}` : '';
            const res = await getRequest(
                `/modules/temp-modules/by-name/${encodeURIComponent(moduleName)}${query}`
            );

            if (!res || res.code !== 200) {
                throw new Error(res?.message || 'Failed to load module data.');
            }

            const module = res?.body;
            if (!module) throw new Error(`Module "${moduleName}" not found.`);

            setState({
                module,
                sections: module.sections || [],
                loading: false,
                error: null,
            });
        } catch (err) {
            setState(prev => ({ ...prev, loading: false, error: err.message }));
        }
    }, [companyId, moduleName]);

    useEffect(() => { fetchData(); }, [fetchData]);

    return { ...state, refetch: fetchData };
}
